import { Box, MenuItem, Select, Typography } from '@mui/material';
import { useIntl, FormattedMessage } from 'react-intl';
import type { RoiId } from '~/domain/changes/ids';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { dicomColorToRgbString } from '~/utils/color';
import { mutedNoteSx, warningSx } from './constraintStyles';

interface InsideConstraintSectionProps {
    /**
     * The modifier is off. Its settings show but are held, so nothing can
     * be set that does not apply: turn the modifier on to change them.
     */
    disabled: boolean;
    /** The selected bounding structure, or null. */
    roiId: RoiId | null;
    /** Structures selectable as the bounding structure. */
    candidates: readonly RoiReadModel[];
    /**
     * Why the enabled constraint clips nothing, as a message id. Undefined
     * while it applies.
     */
    inactiveReasonId?: string;
    /**
     * Erase mode is active: the selection is persisted but not applied to the
     * stroke. The controls stay interactive so the user can configure them for
     * the next draw; a note explains they are dormant right now.
     */
    erasing: boolean;
    onSelect: (roiId: RoiId) => void;
}

/**
 * The settings of the Inside (bounding) constraint, which clips drawing to
 * the interior of one structure: the structure to clip to. It is presentational:
 * all state lives in the parent orchestrator.
 */
export default function InsideConstraintSection({
    disabled,
    roiId,
    candidates,
    inactiveReasonId,
    erasing,
    onSelect,
}: InsideConstraintSectionProps) {
    const intl = useIntl();
    const selectedValue =
        roiId !== null && candidates.some((r) => r.roiId === roiId)
            ? roiId
            : '';

    return (
        <>
            {erasing && (
                <Typography sx={mutedNoteSx}>
                    <FormattedMessage id="constraintNotAppliedWhileErasing" />
                </Typography>
            )}
            <Select<RoiId | ''>
                value={selectedValue}
                onChange={(e) => {
                    const picked = candidates.find(
                        (r) => r.roiId === e.target.value
                    );
                    if (picked) {
                        onSelect(picked.roiId);
                    }
                }}
                disabled={disabled || candidates.length === 0}
                displayEmpty
                size="small"
                fullWidth
                renderValue={(value) => {
                    if (value === '') {
                        return intl.formatMessage({
                            id: 'boundingConstraintPlaceholder',
                        });
                    }
                    const roi = candidates.find((r) => r.roiId === value);
                    if (!roi) {
                        return '';
                    }
                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <StructureSwatch
                                color={dicomColorToRgbString(roi.color)}
                            />
                            <span>{roi.name}</span>
                        </Box>
                    );
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                sx={{ fontSize: 12 }}
            >
                {candidates.map((roi) => (
                    <MenuItem
                        key={roi.roiId}
                        value={roi.roiId}
                        sx={{
                            fontSize: 12,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <StructureSwatch
                            color={dicomColorToRgbString(roi.color)}
                        />
                        {roi.name}
                    </MenuItem>
                ))}
            </Select>
            {inactiveReasonId !== undefined && (
                <Typography sx={warningSx}>
                    <FormattedMessage id={inactiveReasonId} />
                </Typography>
            )}
        </>
    );
}
